// src/authpages/group/StudyGroup.js
import React, { useState, useEffect } from 'react';
import { Search, Users, Plus, Edit } from 'lucide-react';
import { auth, db } from '../../firebase';
import { getFriendsWithProfiles } from '../dashboard/friendsUtils';

// Import components
import StudyMethods from './StudyMethods';
import ActiveGroups from './ActiveGroups';
import CreateGroupForm from './CreateGroupForm';
import ManageGroupForm from './ManageGroupForm';
import GroupSession from './GroupSession'; 

// Study methods data
import { 
  studyMethods, 
  timerMethods, 
  createStudyGroup,
  fetchUserGroups,
  joinStudyGroup,
  leaveStudyGroup
} from './groupUtils';

// Firebase imports
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';

const StudyGroup = () => {
  const [activeTab, setActiveTab] = useState('browse');
  
  // Friend Selection State
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Active Groups State
  const [activeGroups, setActiveGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isInSession, setIsInSession] = useState(false);
  
  // Edit Group State
  const [isEditing, setIsEditing] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  
  // Fetch friends and active groups when component mounts
  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser) return;
      
      setLoading(true);
      try {
        // Fetch friends
        const friendsList = await getFriendsWithProfiles(auth.currentUser.uid);
        const verifiedFriends = friendsList.filter(friend => friend.verified);
        setFriends(verifiedFriends);
        
        // Fetch active groups
        const groups = await fetchUserGroups(auth.currentUser.uid, db);
        setActiveGroups(groups);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Create group with Firebase integration
  const createGroup = async (groupData) => {
    try {
      setLoading(true);
      // Use the createStudyGroup function from groupUtils
      const newGroup = await createStudyGroup(groupData, db);
      
      // Update local state - ensure we're not adding a duplicate
      setActiveGroups(prev => {
        // Check if the group already exists in our state
        const exists = prev.some(group => group.id === newGroup.id);
        if (exists) {
          // If it exists, replace it
          return prev.map(group => group.id === newGroup.id ? newGroup : group);
        } else {
          // If it doesn't exist, add it
          return [...prev, newGroup];
        }
      });
      
      setActiveTab('active');
      return newGroup;
    } catch (error) {
      console.error("Error creating study group:", error);
      setError("Failed to create group. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Join group with Firebase integration
  const joinGroup = async (groupId) => {
    try {
      setLoading(true);
      // Use the joinStudyGroup function from groupUtils
      await joinStudyGroup(auth.currentUser.uid, groupId, db);
      
      // Get the specific updated group instead of refetching all groups
      const groupRef = doc(db, 'studyGroups', groupId);
      const updatedGroupDoc = await getDoc(groupRef);
      
      if (updatedGroupDoc.exists()) {
        const updatedGroup = {
          id: groupId,
          ...updatedGroupDoc.data()
        };
        
        // Update only this specific group in local state
        setActiveGroups(prev => 
          prev.map(group => group.id === groupId ? updatedGroup : group)
        );
      }
    } catch (error) {
      console.error("Error joining study group:", error);
      setError("Failed to join group. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Edit group handler
  const handleEditGroup = (group) => {
    // Make a deep copy to avoid reference issues
    setGroupToEdit(JSON.parse(JSON.stringify(group)));
    setIsEditing(true);
    setActiveTab('edit');
  };

  // Update group with Firebase integration
  const updateGroup = async (updatedGroupData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Create a reference to the group document
      const groupRef = doc(db, 'studyGroups', updatedGroupData.id);
      
      // First check if the document exists
      const docSnap = await getDoc(groupRef);
      
      if (!docSnap.exists()) {
        console.error(`Document with ID ${updatedGroupData.id} does not exist`);
        setError(`Cannot find the group to update. It may have been deleted.`);
        
        // Remove from local state
        setActiveGroups(prev => prev.filter(group => group.id !== updatedGroupData.id));
        
        // Exit edit mode
        setIsEditing(false);
        setGroupToEdit(null);
        setActiveTab('active');
        setLoading(false);
        return;
      }
      
      console.log(`Updating group document: ${updatedGroupData.id}`);
      
      // Prepare the update object
      const updateObject = {
        name: updatedGroupData.name,
        subject: updatedGroupData.subject,
        studyMethod: updatedGroupData.studyMethod,
        timerMethod: updatedGroupData.timerMethod,
        timerSettings: updatedGroupData.timerSettings,
        members: updatedGroupData.members,
        memberCount: updatedGroupData.memberCount,
        lastActive: new Date().toISOString()
      };
      
      // Update the group document in Firestore
      await updateDoc(groupRef, updateObject);
      
      console.log("Document successfully updated");
      
      // Handle member changes
      if (groupToEdit) {
        // Remove members who are no longer in the group
        const removedMembers = groupToEdit.members.filter(
          memberId => !updatedGroupData.members.includes(memberId)
        );
        
        for (const memberId of removedMembers) {
          try {
            await leaveStudyGroup(memberId, updatedGroupData.id, db);
          } catch (memberError) {
            console.error(`Error removing member ${memberId}:`, memberError);
          }
        }
        
        // Add new members to the group
        const newMembers = updatedGroupData.members.filter(
          memberId => !groupToEdit.members.includes(memberId)
        );
        
        for (const memberId of newMembers) {
          try {
            await joinStudyGroup(memberId, updatedGroupData.id, db);
          } catch (memberError) {
            console.error(`Error adding member ${memberId}:`, memberError);
          }
        }
      }
      
      // Update local state directly - CRITICAL for preventing duplicates
      setActiveGroups(prevGroups => {
        return prevGroups.map(group => {
          if (group.id === updatedGroupData.id) {
            // Return a merged version that preserves fields not in updateObject
            return {
              ...group,                 // Keep all original fields
              ...updateObject,          // Override with updated fields
              id: updatedGroupData.id   // Ensure ID is preserved
            };
          }
          return group;
        });
      });
      
      // Exit edit mode
      setIsEditing(false);
      setGroupToEdit(null);
      setActiveTab('active');
      
    } catch (error) {
      console.error("Error updating study group:", error);
      setError(`Failed to update group: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Delete group with Firebase integration
  const deleteGroup = async (groupId) => {
    try {
      setLoading(true);
      setError(null);
      
      // First check if the document exists in Firestore
      const groupRef = doc(db, 'studyGroups', groupId);
      const docSnap = await getDoc(groupRef);
      
      if (!docSnap.exists()) {
        console.log(`Group with ID ${groupId} doesn't exist in Firestore, removing from local state only`);
        // Update local state to remove the group
        setActiveGroups(prev => prev.filter(group => group.id !== groupId));
        
        // Exit edit mode
        setIsEditing(false);
        setGroupToEdit(null);
        setActiveTab('active');
        return;
      }
      
      // Get the group data 
      const groupToDelete = docSnap.data();
      
      // Check if current user is the creator
      if (groupToDelete.creator !== auth.currentUser.uid) {
        throw new Error("Only the group creator can delete this group");
      }
      
      // Remove the group from all members' studyGroups array
      for (const memberId of groupToDelete.members) {
        try {
          // Clean up user documents
          await leaveStudyGroup(memberId, groupId, db);
        } catch (memberError) {
          console.error(`Error removing group from member ${memberId}:`, memberError);
        }
      }
      
      // Delete the group document
      await deleteDoc(groupRef);
      console.log(`Successfully deleted group ${groupId}`);
      
      // Update local state
      setActiveGroups(prev => prev.filter(group => group.id !== groupId));
      
      // Exit edit mode
      setIsEditing(false);
      setGroupToEdit(null);
      setActiveTab('active');
      
    } catch (error) {
      console.error("Error deleting study group:", error);
      setError(`Failed to delete group: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Function to get friend name by ID
  const getFriendNameById = (friendId) => {
    const friend = friends.find(f => f.id === friendId);
    return friend ? friend.fullName : 'Unknown User';
  };

  // Function to start a group session
  const startGroupSession = (group) => {
    setSelectedGroup(group);
    setIsInSession(true);
  };

  // Function to end a group session
  const endGroupSession = () => {
    setSelectedGroup(null);
    setIsInSession(false);
    setActiveTab('active');
  };

  // Cancel editing
  const cancelEdit = () => {
    setIsEditing(false);
    setGroupToEdit(null);
    setActiveTab('active');
  };

  if (isInSession && selectedGroup) {
    return (
      <GroupSession 
        group={selectedGroup}
        currentUserId={auth.currentUser?.uid}
        getFriendNameById={getFriendNameById}
        endSession={endGroupSession}
      />
    );
  }

  return (
    <div className="bg-white min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-blue-600 flex items-center">
          <Users className="mr-3" /> Study Groups
        </h1>

        {/* Tabs */}
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          {[
            { key: 'browse', label: 'Browse Methods', icon: Search },
            { key: 'active', label: 'Active Groups', icon: Users },
            { key: 'create', label: 'Create Group', icon: Plus },
            ...(!isEditing ? [] : [{ key: 'edit', label: 'Edit Group', icon: Edit }])
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`flex-1 py-2 px-4 flex items-center justify-center ${
                activeTab === key ? 'bg-blue-500 text-white' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab(key)}
            >
              <Icon className="mr-2" /> {label}
            </button>
          ))}
        </div>

        {/* Content Area - Conditionally render the appropriate component based on active tab */}
        {activeTab === 'browse' && (
          <StudyMethods studyMethods={studyMethods} />
        )}

        {activeTab === 'active' && (
          <ActiveGroups 
            activeGroups={activeGroups} 
            currentUserId={auth.currentUser?.uid}
            getFriendNameById={getFriendNameById}
            joinGroup={joinGroup}
            onCreateClick={() => setActiveTab('create')}
            onGroupSelect={startGroupSession}
            onEditGroup={handleEditGroup}
            loading={loading}
            error={error}
          />
        )}

        {activeTab === 'create' && (
          <CreateGroupForm
            friends={friends}
            loading={loading}
            error={error}
            studyMethods={studyMethods}
            timerMethods={timerMethods}
            currentUserId={auth.currentUser?.uid}
            onCreateGroup={createGroup}
          />
        )}

        {activeTab === 'edit' && groupToEdit && (
          <ManageGroupForm
            group={groupToEdit}
            friends={friends}
            loading={loading}
            error={error}
            studyMethods={studyMethods}
            timerMethods={timerMethods}
            currentUserId={auth.currentUser?.uid}
            onUpdateGroup={updateGroup}
            onDeleteGroup={deleteGroup}
            onCancel={cancelEdit}
          />
        )}
      </div>
    </div>
  );
};

export default StudyGroup;